import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, IconButton, Chip, Button,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Alert, Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Add as NewChatIcon,
  SmartToy as AiIcon,
  Person as UserIcon,
  Refresh as RetryIcon,
  Flag as ReportIcon,
  ContentCopy as CopyIcon,
  Build as ToolIcon,
  Settings as SettingsIcon,
  QueryStats as UsageIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiAssistantService from '../../services/ai/aiAssistant.service';
import { getBrandColor, getBrandTint } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { AI_CORRECTIONS_ENABLED } from '../../config/aiFeatures';

const SESSION_KEY = 'ai_session_id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const AI_SETTINGS_PATH = '/settings?section=ai&tab=settings';
const AI_USAGE_PATH = '/ai-assistant/usage';
const PROVIDER_LABELS = { ANTHROPIC: 'Claude', OPENAI: 'OpenAI' };
// Screen height minus the app bar and the layout's top and bottom padding, so the page itself never scrolls
const CHAT_HEIGHT = { xs: 'calc(100vh - 88px)', sm: 'calc(100vh - 96px)', md: 'calc(100vh - 112px)' };

const errorText = (err) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Something went wrong';
const errorCode = (err) => err?.response?.data?.code;

// ── Markdown Components (styled for MUI) ─────────────────────────

const MdComponents = {
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        {children}
      </table>
    </Box>
  ),
  thead: ({ children }) => (
    <thead style={{ borderBottom: '2px solid rgba(128, 128, 128, 0.35)' }}>{children}</thead>
  ),
  th: ({ children }) => (
    <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: '0.75rem', opacity: 0.75 }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ padding: '5px 10px', borderBottom: '1px solid rgba(128, 128, 128, 0.2)', fontSize: '0.8rem' }}>
      {children}
    </td>
  ),
  p: ({ children }) => (
    <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6, mb: 0.75 }}>{children}</Typography>
  ),
  h2: ({ children }) => (
    <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mb: 0.75, mt: 1 }}>{children}</Typography>
  ),
  h3: ({ children }) => (
    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, mb: 0.5, mt: 0.75 }}>{children}</Typography>
  ),
  li: ({ children }) => (
    <li style={{ fontSize: '0.83rem', lineHeight: 1.5 }}>{children}</li>
  ),
  strong: ({ children }) => (
    <strong style={{ fontWeight: 600 }}>{children}</strong>
  ),
};

// ── Main Component ───────────────────────────────────────────────

const AiChat = ({ currentUser }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored && UUID_RE.test(stored) ? stored : null;
  });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportText, setReportText] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [copied, setCopied] = useState(null);

  const messagesRef = useRef(null);
  const inputRef = useRef(null);
  const initialSessionRef = useRef(sessionId);

  const brandColor = getBrandColor();
  const brandTint = getBrandTint();

  const canOpenSettings = can(currentUser, 'settings', 'read');
  const suggestedQuestions = aiAssistantService.getSuggestedQuestions(currentUser, status?.available_categories || []);
  const ready = Boolean(status?.enabled && status?.configured);
  const percentUsed = usage?.percent_used;
  const blocked = limitReached || (percentUsed != null && percentUsed >= 100);

  // ── Status gate ──────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    try {
      const s = await aiAssistantService.getStatus();
      setStatus(s);
      setUsage(s?.usage || null);
      setStatusError(null);
    } catch (err) {
      setStatusError(errorText(err));
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // ── Auto-scroll ──────────────────────────────────────────────

  useEffect(() => {
    const box = messagesRef.current;
    if (box) box.scrollTo({ top: box.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  // ── Restore the stored session once the assistant is ready ───

  useEffect(() => {
    const restoreId = initialSessionRef.current;
    if (!ready || !restoreId) return;
    initialSessionRef.current = null;
    const loadHistory = async () => {
      try {
        const history = await aiAssistantService.getHistory(restoreId);
        const restored = [];
        (history?.exchanges || []).forEach(ex => {
          restored.push({ role: 'user', content: ex.user_message, timestamp: ex.timestamp });
          if (ex.ai_response) {
            restored.push({
              role: 'assistant',
              content: ex.ai_response,
              exchangeId: ex.exchange_id,
              toolsUsed: ex.tools_used,
              timestamp: ex.timestamp,
            });
          }
        });
        setMessages(restored);
      } catch (e) {
        sessionStorage.removeItem(SESSION_KEY);
        setSessionId(null);
      }
    };
    loadHistory();
  }, [ready]);

  // ── Send Message ─────────────────────────────────────────────

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading || blocked) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);

    try {
      const res = await aiAssistantService.chat(sessionId, msg);
      if (res.session_id && res.session_id !== sessionId) {
        sessionStorage.setItem(SESSION_KEY, res.session_id);
        setSessionId(res.session_id);
      }
      setUsage(res.usage || null);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.response,
        exchangeId: res.exchange_id,
        toolsUsed: res.tools_used,
        toolExecutions: res.tool_executions,
        followUps: res.follow_ups,
        tokensUsed: res.tokens_used,
        responseTimeMs: res.response_time_ms,
        timestamp: res.timestamp,
      }]);
    } catch (err) {
      const code = errorCode(err);
      if (code === 'AI_RATE_LIMITED') {
        setLimitReached(true);
      } else if (code === 'AI_NOT_ENABLED' || code === 'AI_NOT_CONFIGURED') {
        loadStatus();
      }
      setMessages(prev => [...prev, {
        role: 'error',
        content: errorText(err),
        retryMessage: code === 'AI_RATE_LIMITED' ? null : msg,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, loading, blocked, sessionId, loadStatus]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── New Chat ─────────────────────────────────────────────────

  const handleNewChat = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    setMessages([]);
    setInput('');
  };

  // ── Copy Response ────────────────────────────────────────────

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied(idx);
    setTimeout(() => setCopied(null), 2000);
  };

  // ── Report Issue ─────────────────────────────────────────────

  const handleReport = (msg, idx) => {
    setReportTarget({ message: msg, index: idx });
    setReportText('');
    setReportSent(false);
    setReportError(null);
    setReportOpen(true);
  };

  const submitReport = async () => {
    try {
      await aiAssistantService.reportCorrection(reportTarget?.message?.exchangeId, reportText.trim());
      setReportSent(true);
      setTimeout(() => setReportOpen(false), 1500);
    } catch (err) {
      setReportError(errorText(err));
    }
  };

  // ── Retry ────────────────────────────────────────────────────

  const handleRetry = (retryMsg) => {
    setMessages(prev => prev.filter(m => m.role !== 'error'));
    sendMessage(retryMsg);
  };

  // ── Gate screens (loading / error / not enabled / no key) ────

  const gate = (title, text, showSettings) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHAT_HEIGHT, p: 3 }}>
      <Paper elevation={0} sx={{ p: 4, maxWidth: 460, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <AiIcon sx={{ fontSize: 44, color: 'text.secondary', opacity: 0.35, mb: 1 }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{text}</Typography>
        {showSettings && canOpenSettings && (
          <Button
            variant="contained" size="small"
            startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate(AI_SETTINGS_PATH)}
            sx={{ mt: 2.5, textTransform: 'none' }}
          >
            Open AI Settings
          </Button>
        )}
      </Paper>
    </Box>
  );

  if (statusError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button size="small" onClick={loadStatus}>Retry</Button>}>
          {statusError}
        </Alert>
      </Box>
    );
  }
  if (!status) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHAT_HEIGHT, gap: 1 }}>
        <CircularProgress size={18} sx={{ color: brandColor }} />
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Checking AI Assistant…</Typography>
      </Box>
    );
  }
  if (!status.enabled) {
    return gate("AI Assistant isn't enabled",
      canOpenSettings
        ? 'Turn it on for your typing center in AI Settings.'
        : 'Ask your typing center admin to turn it on in AI Settings.',
      true);
  }
  if (!status.configured) {
    const provider = PROVIDER_LABELS[status.provider] || status.provider;
    return gate('No API key set up',
      `The AI Assistant uses ${provider}, but no ${provider} API key is set up and switched on. Ask your admin to add one in Settings → AI Settings.`,
      true);
  }

  // ── Render ───────────────────────────────────────────────────

  const isEmpty = messages.length === 0 && !loading;
  const providerLabel = PROVIDER_LABELS[status.provider] || status.provider;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: CHAT_HEIGHT, minHeight: 420,
      border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.default',
    }}>

      {/* ── Header ────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon sx={{ color: brandColor, fontSize: 24 }} />
          <Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>AI Assistant</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {providerLabel} · {status.model} · {messages.filter(m => m.role === 'user').length} messages
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {canOpenSettings && (
            <>
              <Tooltip title="AI usage">
                <IconButton
                  size="small"
                  onClick={() => navigate(AI_USAGE_PATH)}
                  aria-label="AI usage"
                  sx={{ color: brandColor, bgcolor: brandTint, '&:hover': { bgcolor: brandTint, opacity: 0.85 } }}
                >
                  <UsageIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
            </>
          )}
          <Button
            size="small"
            startIcon={<NewChatIcon sx={{ fontSize: 16 }} />}
            onClick={handleNewChat}
            disabled={loading}
            sx={{ fontSize: '0.8rem', textTransform: 'none' }}
          >
            New Chat
          </Button>
        </Box>
      </Box>

      {/* ── Messages Area ─────────────────────────────────────── */}
      <Box ref={messagesRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>

        {/* Empty state — suggested questions */}
        {isEmpty && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <AiIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
              <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                How can I help you today?
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                Ask about work orders, invoices, accounting, or anything about your typing center.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: 600 }}>
              {suggestedQuestions.map((q, i) => (
                <Chip
                  key={i}
                  label={q}
                  size="small"
                  onClick={() => sendMessage(q)}
                  disabled={blocked}
                  sx={{
                    fontSize: '0.78rem', cursor: 'pointer', bgcolor: brandTint,
                    '&:hover': { bgcolor: brandColor, color: '#fff' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Messages */}
        {messages.map((msg, idx) => (
          <Box key={idx} sx={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 2,
          }}>
            {/* AI / Error avatar */}
            {msg.role !== 'user' && (
              <Box sx={{
                width: 30, height: 30, borderRadius: '50%', mr: 1, mt: 0.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: msg.role === 'error' ? 'error.main' : brandTint,
                flexShrink: 0,
              }}>
                <AiIcon sx={{ fontSize: 16, color: msg.role === 'error' ? '#fff' : brandColor }} />
              </Box>
            )}

            {/* Message bubble */}
            <Paper
              elevation={0}
              sx={{
                maxWidth: '75%',
                px: 2, py: 1.5,
                borderRadius: 2,
                bgcolor: msg.role === 'user' ? brandColor : msg.role === 'error' ? 'error.lighter' : 'background.paper',
                color: msg.role === 'user' ? '#fff' : 'text.primary',
                border: msg.role === 'user' ? 'none' : '1px solid',
                borderColor: msg.role === 'error' ? 'error.light' : 'divider',
              }}
            >
              {msg.role === 'user' ? (
                <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {msg.content}
                </Typography>
              ) : msg.role === 'error' ? (
                <Box>
                  <Typography sx={{ fontSize: '0.85rem', color: 'error.main' }}>{msg.content}</Typography>
                  {msg.retryMessage && (
                    <Button
                      size="small"
                      startIcon={<RetryIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleRetry(msg.retryMessage)}
                      sx={{ mt: 1, fontSize: '0.75rem', textTransform: 'none' }}
                    >
                      Retry
                    </Button>
                  )}
                </Box>
              ) : (
                <Box>
                  {/* Markdown content */}
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={MdComponents}>
                    {msg.content}
                  </ReactMarkdown>

                  {/* Tool badges */}
                  {msg.toolsUsed?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <ToolIcon sx={{ fontSize: 13, color: 'text.secondary', mt: 0.3 }} />
                      {msg.toolsUsed.map((t, ti) => (
                        <Chip key={ti} label={t.replace(/_/g, ' ')} size="small"
                          sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'action.selected', color: 'text.secondary' }}
                        />
                      ))}
                      {msg.responseTimeMs && (
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 0.5, mt: 0.3 }}>
                          {(msg.responseTimeMs / 1000).toFixed(1)}s
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Actions row */}
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                    <Tooltip title={copied === idx ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={() => handleCopy(msg.content, idx)}>
                        <CopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {AI_CORRECTIONS_ENABLED && msg.exchangeId && (
                      <Tooltip title="Report issue">
                        <IconButton size="small" onClick={() => handleReport(msg, idx)}>
                          <ReportIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* User avatar */}
            {msg.role === 'user' && (
              <Box sx={{
                width: 30, height: 30, borderRadius: '50%', ml: 1, mt: 0.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'action.selected', flexShrink: 0,
              }}>
                <UserIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </Box>
            )}
          </Box>
        ))}

        {/* Loading indicator */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: brandTint, flexShrink: 0,
            }}>
              <AiIcon sx={{ fontSize: 16, color: brandColor }} />
            </Box>
            <Paper elevation={0} sx={{ px: 2, py: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: brandColor }} />
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Thinking...</Typography>
              </Box>
            </Paper>
          </Box>
        )}

      </Box>

      {/* ── Input Area ────────────────────────────────────────── */}
      <Box sx={{
        px: 2, py: 1.25, flexShrink: 0,
        borderTop: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper',
      }}>
        {/* Daily limit: warning from 80%, blocked at 100% */}
        {blocked ? (
          <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.78rem' }}>
            Daily AI limit reached. It resets at midnight UAE time.
          </Alert>
        ) : percentUsed != null && percentUsed >= 80 && (
          <Alert severity="warning" sx={{ mb: 1, py: 0, fontSize: '0.78rem' }}>
            You have used {percentUsed}% of today&apos;s AI limit
            ({(usage.tokens_today || 0).toLocaleString()} of {(usage.max_tokens_per_day || 0).toLocaleString()} tokens).
          </Alert>
        )}

        {/* Contextual follow-up suggestions based on last AI response */}
        {!isEmpty && messages.length > 0 && !loading && !blocked && (() => {
          const lastAi = [...messages].reverse().find(m => m.role === 'assistant');
          const followUps = lastAi?.followUps || [];
          if (followUps.length === 0) return null;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {followUps.map((q, i) => (
                <Chip
                  key={i}
                  label={q}
                  size="small"
                  variant="outlined"
                  onClick={() => sendMessage(q)}
                  sx={{ fontSize: '0.7rem', cursor: 'pointer', borderColor: 'divider', '&:hover': { bgcolor: brandTint } }}
                />
              ))}
            </Box>
          );
        })()}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask about work orders, invoices, accounting..."
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 2000))}
            onKeyDown={handleKeyDown}
            disabled={loading || blocked}
            size="small"
            sx={{
              '& .MuiInputBase-input': { fontSize: '0.85rem' },
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />
          <IconButton
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading || blocked}
            sx={{
              bgcolor: brandColor, color: '#fff',
              '&:hover': { bgcolor: brandColor, opacity: 0.9 },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              width: 40, height: 40,
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            Chats are saved and visible to your typing center admin.
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            {input.length}/2000
          </Typography>
        </Box>
      </Box>

      {/* ── Report Issue Dialog ────────────────────────────────── */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Report Issue</DialogTitle>
        <DialogContent>
          {reportSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Thank you for the feedback. This will help improve future responses.
            </Alert>
          ) : (
            <>
              <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mb: 1.5 }}>
                What was wrong with this response?
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., The invoice count was wrong — it showed 3 but there are only 2 unpaid invoices"
                value={reportText}
                onChange={(e) => setReportText(e.target.value.slice(0, 2000))}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }}
              />
              {reportError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>{reportError}</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)} size="small" sx={{ textTransform: 'none' }}>
            {reportSent ? 'Close' : 'Cancel'}
          </Button>
          {!reportSent && (
            <Button
              variant="contained" size="small"
              onClick={submitReport}
              disabled={!reportText.trim()}
              sx={{ textTransform: 'none' }}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AiChat;
